import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export default function DebtDialog({ open, onClose, onSave, initialData }) {
  const isEdit = !!initialData;
  const [form, setForm] = useState(
    initialData ? {
      name: initialData.name, day_due: initialData.day_due,
      balance: initialData.balance, promo_balance: initialData.promo_balance,
      apr_now: initialData.apr_now, promo_until: initialData.promo_until || "",
      apr_after: initialData.apr_after, statement_min: initialData.statement_min,
      current_balance: initialData.current_balance,
      is_deferred_interest: initialData.is_deferred_interest, notes: initialData.notes || "",
      min_payment_type: initialData.min_payment_type || "percentage",
      min_payment_pct: initialData.min_payment_pct ?? 2.0,
      min_payment_floor: initialData.min_payment_floor ?? 5.0,
      promo_instalment: initialData.promo_instalment ?? 0,
      retail_min_pct: initialData.retail_min_pct ?? 2.0,
      credit_limit: initialData.credit_limit ?? 0,
    } : {
      name: "", day_due: 1, balance: 0, promo_balance: 0,
      apr_now: "0.00%", promo_until: "", apr_after: "0.00%",
      statement_min: 0, current_balance: 0,
      is_deferred_interest: false, notes: "",
      min_payment_type: "percentage", min_payment_pct: 2.0, min_payment_floor: 5.0,
      promo_instalment: 0, retail_min_pct: 2.0,
      credit_limit: 0,
    }
  );

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const minType = form.min_payment_type;
  const retailBal = Number(form.balance);
  const promoBal = Number(form.promo_balance);
  const totalBal = retailBal + promoBal;

  const calcMin = useMemo(() => {
    if (minType === "percentage" && totalBal > 0) {
      return Math.max(totalBal * Number(form.min_payment_pct) / 100, Number(form.min_payment_floor));
    } else if (minType === "split" && retailBal > 0) {
      const aprVal = parseFloat((form.apr_now || "0%").replace("% APR", "").replace("%", "").split("/").map(s => s.trim()).filter(s => parseFloat(s) > 0)[0] || "0");
      const retailInterest = retailBal * (aprVal / 100) / 12;
      const promoInst = Math.min(Number(form.promo_instalment), promoBal);
      const retailMin = retailBal * Number(form.retail_min_pct) / 100;
      return Math.max(retailInterest + promoInst + retailMin, Number(form.min_payment_floor));
    }
    return Number(form.statement_min);
  }, [minType, totalBal, retailBal, promoBal, form.min_payment_pct, form.min_payment_floor, form.promo_instalment, form.retail_min_pct, form.apr_now, form.statement_min]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const stmtMin = minType === "fixed" ? Number(form.statement_min) : Math.round(calcMin * 100) / 100;
    // Prefer the user's typed current_balance; fall back to auto (retail+promo)
    // only if the field is empty / NaN.
    const typedCurrent = Number(form.current_balance);
    const currentBalance = (form.current_balance === "" || Number.isNaN(typedCurrent))
      ? totalBal : typedCurrent;
    onSave({
      ...form,
      day_due: Number(form.day_due), balance: retailBal, promo_balance: promoBal,
      statement_min: stmtMin, current_balance: currentBalance,
      min_payment_pct: Number(form.min_payment_pct), min_payment_floor: Number(form.min_payment_floor),
      promo_instalment: Number(form.promo_instalment), retail_min_pct: Number(form.retail_min_pct),
      credit_limit: Number(form.credit_limit) || 0,
    });
  };

  const typeBtn = (type, label) => (
    <button type="button" data-testid={`min-type-${type}`} onClick={() => handleChange("min_payment_type", type)}
      className={`px-2.5 py-1 text-[11px] font-medium transition-colors whitespace-nowrap ${minType === type ? "bg-black text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg rounded-sm border border-gray-200 max-h-[90vh] overflow-y-auto" data-testid="debt-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg font-semibold tracking-tight">{isEdit ? "Edit Debt" : "Add New Debt"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name + Day Due */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Debt Name</Label>
              <Input data-testid="debt-name-input" value={form.name} onChange={(e) => handleChange("name", e.target.value)} className="rounded-sm mt-1" required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Day Due</Label>
              <Input data-testid="debt-day-input" type="number" min="1" max="31" value={form.day_due} onChange={(e) => handleChange("day_due", e.target.value)} className="rounded-sm mt-1 font-mono-data" />
            </div>
          </div>

          {/* RETAIL SECTION */}
          <div className="border border-gray-200 rounded-sm p-3 space-y-3">
            <h4 className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Retail (Interest-Bearing)</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Balance (£)</Label>
                <Input data-testid="debt-balance-input" type="number" step="0.01" value={form.balance} onChange={(e) => handleChange("balance", e.target.value)} className="rounded-sm mt-1 font-mono-data" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">APR</Label>
                <Input data-testid="debt-apr-now-input" value={form.apr_now} onChange={(e) => handleChange("apr_now", e.target.value)} className="rounded-sm mt-1 font-mono-data" placeholder="e.g. 23.9%" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-gray-500">Credit Limit (£) <span className="text-gray-400">— optional, used for utilisation %</span></Label>
                <Input data-testid="debt-credit-limit-input" type="number" step="1" min="0" value={form.credit_limit} onChange={(e) => handleChange("credit_limit", e.target.value)} className="rounded-sm mt-1 font-mono-data" placeholder="0 = hide utilisation" />
              </div>
            </div>
          </div>

          {/* PROMO SECTION */}
          <div className="border border-gray-200 rounded-sm p-3 space-y-3">
            <h4 className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Promo (0% Interest)</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Balance (£)</Label>
                <Input data-testid="debt-promo-input" type="number" step="0.01" value={form.promo_balance} onChange={(e) => handleChange("promo_balance", e.target.value)} className="rounded-sm mt-1 font-mono-data" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">0% Until</Label>
                <Input data-testid="debt-promo-until-input" value={form.promo_until} onChange={(e) => handleChange("promo_until", e.target.value)} className="rounded-sm mt-1 font-mono-data" placeholder="Jun-28" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">APR After</Label>
                <Input data-testid="debt-apr-after-input" value={form.apr_after} onChange={(e) => handleChange("apr_after", e.target.value)} className="rounded-sm mt-1 font-mono-data" placeholder="23.9%" />
              </div>
            </div>
          </div>

          {/* MIN PAYMENT RULE */}
          <div className="border border-gray-200 rounded-sm p-3 bg-gray-50 space-y-3">
            <div className="flex items-center gap-3">
              <Label className="text-xs uppercase tracking-wider text-gray-500 shrink-0">Min Payment Rule</Label>
              <div className="flex rounded-sm overflow-hidden border border-gray-300">
                {typeBtn("percentage", "% of Balance")}
                {typeBtn("split", "Split (PayPal)")}
                {typeBtn("fixed", "Fixed")}
              </div>
            </div>

            {minType === "percentage" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Percentage (%)</Label>
                  <Input data-testid="debt-min-pct-input" type="number" step="0.01" min="0.01" max="10" value={form.min_payment_pct} onChange={(e) => handleChange("min_payment_pct", e.target.value)} className="rounded-sm mt-1 font-mono-data" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Floor (£)</Label>
                  <Input data-testid="debt-min-floor-input" type="number" step="0.01" min="0" value={form.min_payment_floor} onChange={(e) => handleChange("min_payment_floor", e.target.value)} className="rounded-sm mt-1 font-mono-data" />
                </div>
              </div>
            )}

            {minType === "split" && (
              <div className="space-y-2">
                <p className="text-[11px] text-gray-400">Interest on retail + promo instalment + % of retail</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Retail Min (%)</Label>
                    <Input data-testid="debt-retail-min-pct-input" type="number" step="0.01" min="0" max="10" value={form.retail_min_pct} onChange={(e) => handleChange("retail_min_pct", e.target.value)} className="rounded-sm mt-1 font-mono-data" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Instalment (£)</Label>
                    <Input data-testid="debt-promo-instalment-input" type="number" step="0.01" min="0" value={form.promo_instalment} onChange={(e) => handleChange("promo_instalment", e.target.value)} className="rounded-sm mt-1 font-mono-data" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Floor (£)</Label>
                    <Input data-testid="debt-split-floor-input" type="number" step="0.01" min="0" value={form.min_payment_floor} onChange={(e) => handleChange("min_payment_floor", e.target.value)} className="rounded-sm mt-1 font-mono-data" />
                  </div>
                </div>
              </div>
            )}

            {minType === "fixed" && (
              <div>
                <Label className="text-xs text-gray-500">Fixed Min Payment (£)</Label>
                <Input data-testid="debt-min-input" type="number" step="0.01" value={form.statement_min} onChange={(e) => handleChange("statement_min", e.target.value)} className="rounded-sm mt-1 font-mono-data" />
              </div>
            )}

            {minType !== "fixed" && totalBal > 0 && (
              <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                Calc. min: <span className="font-mono-data font-semibold text-gray-700">£{calcMin.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Editable current balance + deferred + notes. Pre-fills to
              (retail + promo) but can be overridden — users often need to
              type the real balance from their statement. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Current Balance (£)</Label>
              <Input
                type="number" step="0.01"
                data-testid="debt-current-balance-input"
                value={form.current_balance ?? ""}
                onChange={(e) => {
                  handleChange("current_balance", e.target.value);
                  handleChange("_current_balance_touched", true);
                }}
                className="rounded-sm mt-1 font-mono-data text-sm"
              />
              <p className="text-[10px] text-gray-400 mt-0.5">
                Auto: Retail + Promo = £{totalBal.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                {!form._current_balance_touched && (
                  <button
                    type="button"
                    data-testid="debt-current-balance-reset"
                    onClick={() => handleChange("current_balance", totalBal)}
                    className="ml-2 text-blue-600 hover:underline"
                  >use auto</button>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3 border border-gray-200 rounded-sm p-3 bg-gray-50">
              <Switch data-testid="debt-deferred-switch" checked={form.is_deferred_interest} onCheckedChange={(v) => handleChange("is_deferred_interest", v)} />
              <div>
                <Label className="text-sm font-medium text-gray-700">Deferred Interest</Label>
                <p className="text-[10px] text-gray-500">Retroactive if unpaid</p>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Notes</Label>
            <Input data-testid="debt-notes-input" value={form.notes} onChange={(e) => handleChange("notes", e.target.value)} className="rounded-sm mt-1" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm" data-testid="debt-dialog-cancel">Cancel</Button>
            <Button type="submit" className="rounded-sm bg-black text-white hover:bg-gray-800" data-testid="debt-dialog-save">{isEdit ? "Update" : "Add Debt"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
